import { gql, useQuery } from '@apollo/client';
import { Stack } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { EventAttachmentsCard } from '@/components/EventAttachmentsCard';
import { EventDescriptionCard } from '@/components/EventDescriptionCard';
import { LocationCard } from '@/components/LocationCard';
import { MembershipRenewalModal } from '@/components/MembershipRenewalModal';
import { usePermissions } from '@/hooks/usePermissions';
import { logError } from '@/imports/logging/ClientLogger';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { LogInToView } from '@/imports/ui/chakra/components/LogInToView';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { EventListEmpty } from '@/imports/ui/components/EventList';
import { Coffee, CoffeeAside, CoffeeMain, CoffeeNav } from '@/layouts/Coffee';

import {
  EventPage_EventQuery,
  EventPage_EventQueryVariables,
} from './graphql-types/EventPage_EventQuery';

import { HeaderBannerCard } from '../layouts/components/HeaderBannerCard';
import { SimpleEventSponsorsAside } from './components/SimpleEventSponsorsAside';
import { SpeakersCard } from './components/SpeakersCard';
import { useAutoRsvp } from './hooks/useAutoRsvp';

const EVENT_QUERY = gql`
  query EventPage_EventQuery($input: EventInput!) {
    event(input: $input) {
      eventV2 {
        _id
        ...HeaderBannerCard_EventFragment
        ...EventDescriptionCard_EventFragment
        attachments {
          versionFileId
          fileName
        }
        ...LocationCard_EventFragment
        ... on SimpleEventV2 {
          _id
          ...SpeakersCard_SimpleEventFragment
        }
      }
    }
  }
  ${HeaderBannerCard.eventFragment}
  ${EventDescriptionCard.eventFragment}
  ${SpeakersCard.simpleEventFragment}
  ${LocationCard.eventFragment}
`;

export const EventPage: React.FC = () => {
  const router = useRouter();
  const eventId = router.query.eventId.toString();

  useAutoRsvp({ eventId });
  const permissions = usePermissions();

  const { loading, data } = useQuery<
    EventPage_EventQuery,
    EventPage_EventQueryVariables
  >(EVENT_QUERY, {
    variables: {
      input: {
        _id: eventId,
      },
    },
    onError: logError,
  });

  React.useEffect(() => {
    if (data?.event.eventV2?.__typename === 'ConferenceEventV2') {
      router.replace(`/events/conferences/${eventId}`);
    }
  }, [data?.event.eventV2?.__typename, eventId, router]);

  if (data?.event.eventV2 === null) {
    return (
      <Coffee>
        <CoffeeNav />
        <CoffeeMain>
          {loading || permissions.isLoading ? (
            <Loading />
          ) : !data?.event.eventV2 ? (
            !permissions.isAuthenticated ? (
              <LogInToView />
            ) : (
              <Stack spacing="4">
                <EventListEmpty title="There are no events here" />;
              </Stack>
            )
          ) : (
            <Stack spacing="4">
              <EventListEmpty title="There are no events here" />;
            </Stack>
          )}
        </CoffeeMain>
        <CoffeeAside></CoffeeAside>
      </Coffee>
    );
  }

  return (
    <Coffee>
      <CoffeeNav />
      <CoffeeMain>
        {loading || permissions.isLoading ? (
          <Loading />
        ) : !data?.event.eventV2 ? (
          !permissions.isAuthenticated ? (
            <LogInToView />
          ) : (
            <Stack spacing="4">
              <EventListEmpty title="You do not have permission to view this event." />
            </Stack>
          )
        ) : data.event.eventV2.__typename !== 'SimpleEventV2' ? (
          <Loading />
        ) : (
          <Stack spacing="4">
            <HeaderBannerCard event={data.event.eventV2} />
            <EventDescriptionCard event={data.event.eventV2} />
            {data.event.eventV2.attachments.length > 0 && (
              <EventAttachmentsCard
                attachmentIds={data.event.eventV2.attachments.map(
                  (attachment) => attachment.versionFileId
                )}
                title={
                  data.event.eventV2.attachments.length === 1
                    ? 'Attachment'
                    : 'Attachments'
                }
              />
            )}
            <LocationCard event={data.event.eventV2} />
            <SpeakersCard simpleEvent={data.event.eventV2} />
            <MembershipRenewalModal />
          </Stack>
        )}
      </CoffeeMain>
      <CoffeeAside>
        {data?.event.eventV2?.__typename === 'SimpleEventV2' ? (
          <SimpleEventSponsorsAside eventId={eventId} />
        ) : null}
      </CoffeeAside>
    </Coffee>
  );
};
